import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Heart, Minus, Plus, Truck, MapPin, Leaf, ShieldCheck, ChevronLeft } from "lucide-react";
import api, { inr, formatApiError } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Seo } from "../components/Seo";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart, toggleWishlist, wishlist } = useStore();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [subscribe, setSubscribe] = useState(false);
  const [pincode, setPincode] = useState("");
  const [pinResult, setPinResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [rating, setRating] = useState(5);
  const [rName, setRName] = useState("");
  const [rComment, setRComment] = useState("");
  const [zoom, setZoom] = useState({ show: false, x: 0, y: 0 });

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => { setProduct(r.data); setVariant(r.data.variants[0]); setActiveImg(0); });
  }, [slug]);
  useEffect(() => {
    if (product) api.get(`/products/${product.id}/reviews`).then((r) => setReviews(r.data));
  }, [product]);

  if (!product || !variant) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading…</div>;

  const inWishlist = wishlist.includes(product.slug);
  const discount = variant.mrp > variant.price ? Math.round(((variant.mrp - variant.price) / variant.mrp) * 100) : 0;

  const checkPincode = async () => {
    if (!pincode.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.post("/delivery/check", { pincode: pincode.trim() });
      setPinResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setChecking(false); }
  };

  const handleAdd = () => {
    addToCart({
      product_id: product.id, slug: product.slug, name: product.name, variant: variant.weight,
      price: variant.price, qty, image: product.images[0], subscription: subscribe,
    });
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!rName.trim() || !rComment.trim()) { toast.error("Please add your name and review"); return; }
    try {
      await api.post("/reviews", { product_id: product.id, name: rName, rating, comment: rComment });
      toast.success("Thank you for your review!");
      setRName(""); setRComment(""); setRating(5);
      const r = await api.get(`/products/${product.id}/reviews`);
      setReviews(r.data);
      const p = await api.get(`/products/${slug}`);
      setProduct(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setZoom({ show: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 fade-in">
      <Seo title={product.name} description={product.short_desc || product.description} image={product.images[0]} type="product" />
      <Link to="/products" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-gold mb-6"><ChevronLeft size={16} /> Back to shop</Link>
      <div className="grid lg:grid-cols-2 gap-12">
        {/* Images */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <div
            className="relative overflow-hidden rounded-2xl bg-sand aspect-square cursor-crosshair"
            onMouseMove={onMove}
            onMouseLeave={() => setZoom((z) => ({ ...z, show: false }))}
            data-testid="product-main-image"
          >
            <img
              src={product.images[activeImg]}
              alt={product.name}
              className="w-full h-full object-cover"
              style={zoom.show ? { transform: "scale(1.9)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}}
            />
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                  className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${activeImg === i ? "border-gold" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-gold mb-2">{product.category.replace("-", " ")}</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-forest leading-tight">{product.name}</h1>
          <div className="flex items-center gap-2 mt-3">
            {product.rating > 0 ? (
              <>
                <div className="flex">{[1,2,3,4,5].map((s) => <Star key={s} size={16} className={s <= Math.round(product.rating) ? "fill-gold text-gold" : "text-muted-foreground/30"} />)}</div>
                <span className="text-sm text-muted-foreground">{product.rating} • {product.review_count} reviews</span>
              </>
            ) : <span className="text-sm text-muted-foreground">Be the first to review</span>}
          </div>

          <div className="flex items-baseline gap-3 mt-5">
            <span className="font-serif text-4xl text-forest">{inr(variant.price)}</span>
            {variant.mrp > variant.price && <span className="text-lg text-muted-foreground line-through">{inr(variant.mrp)}</span>}
            {discount > 0 && <span className="text-sm bg-gold/20 text-forest px-2 py-0.5 rounded-full">{discount}% off</span>}
          </div>
          <p className="mt-4 text-muted-foreground leading-relaxed">{product.description}</p>

          {/* Variants */}
          <div className="mt-6">
            <div className="text-sm text-forest mb-2">Select Weight</div>
            <div className="flex flex-wrap gap-3">
              {product.variants.map((v) => (
                <button key={v.weight} onClick={() => setVariant(v)} data-testid={`variant-${v.weight}`}
                  className={`px-5 py-2.5 rounded-full text-sm border transition-colors ${variant.weight === v.weight ? "bg-forest text-cream border-forest" : "border-border hover:border-gold"} ${v.stock <= 0 ? "opacity-40" : ""}`}
                  disabled={v.stock <= 0}>
                  {v.weight}
                </button>
              ))}
            </div>
            {variant.stock <= 10 && variant.stock > 0 && <p className="text-xs text-destructive mt-2">Only {variant.stock} left in stock!</p>}
            {variant.stock <= 0 && <p className="text-xs text-destructive mt-2">Out of stock</p>}
          </div>

          {product.subscription && (
            <label className="flex items-center gap-3 mt-5 p-3 rounded-lg bg-sand cursor-pointer" data-testid="subscribe-toggle">
              <input type="checkbox" checked={subscribe} onChange={(e) => setSubscribe(e.target.checked)} className="accent-forest w-4 h-4" />
              <span className="text-sm text-forest">Subscribe & save — monthly delivery of freshness</span>
            </label>
          )}

          {/* Qty + Add */}
          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center border border-border rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3" data-testid="qty-dec"><Minus size={16} /></button>
              <span className="w-8 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="p-3" data-testid="qty-inc"><Plus size={16} /></button>
            </div>
            <Button onClick={handleAdd} disabled={variant.stock <= 0} data-testid="add-to-cart-button"
              className="flex-1 bg-gold text-forest hover:bg-gold-soft rounded-full h-12 text-sm uppercase tracking-[0.15em]">
              Add to Cart • {inr(variant.price * qty)}
            </Button>
            <button onClick={() => toggleWishlist(product.slug, product.name)} data-testid="detail-wishlist"
              className="w-12 h-12 rounded-full border border-border flex items-center justify-center hover:border-gold">
              <Heart size={18} className={inWishlist ? "fill-gold text-gold" : "text-forest"} />
            </button>
          </div>

          {/* Pincode check */}
          <div className="mt-6 p-4 rounded-xl border border-border bg-white">
            <div className="flex items-center gap-2 text-sm text-forest mb-2"><MapPin size={16} className="text-gold" /> Check delivery availability</div>
            <div className="flex gap-2">
              <Input value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="Enter Sitapur pincode" maxLength={6} data-testid="pincode-check-input" className="rounded-full" />
              <Button onClick={checkPincode} disabled={checking} data-testid="pincode-check-button" className="bg-forest text-cream hover:bg-forest-light rounded-full px-6">{checking ? "…" : "Check"}</Button>
            </div>
            {pinResult && (
              <p className={`text-sm mt-3 flex items-center gap-2 ${pinResult.serviceable ? "text-forest" : "text-destructive"}`} data-testid="pincode-result">
                <Truck size={15} /> {pinResult.message}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">Try 261001, 261125 (serviceable) or 261131 (outside range).</p>
          </div>

          {/* Accordions */}
          <Accordion type="single" collapsible className="mt-6" defaultValue="benefits">
            <AccordionItem value="benefits">
              <AccordionTrigger data-testid="accordion-benefits" className="font-serif text-lg"><span className="flex items-center gap-2"><Leaf size={16} className="text-gold" /> Health Benefits</span></AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {product.health_benefits.map((b) => <li key={b} className="flex gap-2"><span className="text-gold">•</span> {b}</li>)}
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="nutrition">
              <AccordionTrigger data-testid="accordion-nutrition" className="font-serif text-lg">Nutritional Information (per 100g)</AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(product.nutrition).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-border py-1.5"><span className="text-muted-foreground">{k}</span><span className="text-forest">{v}</span></div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="origin">
              <AccordionTrigger data-testid="accordion-origin" className="font-serif text-lg"><span className="flex items-center gap-2"><ShieldCheck size={16} className="text-gold" /> Origin</span></AccordionTrigger>
              <AccordionContent><p className="text-sm text-muted-foreground">Sourced from <span className="text-forest font-medium">{product.origin}</span> — selected for superior grade and freshness.</p></AccordionContent>
            </AccordionItem>
            <AccordionItem value="storage">
              <AccordionTrigger data-testid="accordion-storage" className="font-serif text-lg">Storage Instructions</AccordionTrigger>
              <AccordionContent><p className="text-sm text-muted-foreground">{product.storage}</p></AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Reviews */}
      <div className="mt-20 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-serif text-3xl text-forest mb-6">Customer Reviews</h2>
          {reviews.length === 0 ? <p className="text-muted-foreground text-sm">No reviews yet. Share your experience!</p> : (
            <div className="space-y-5">
              {reviews.map((r) => (
                <div key={r.id} className="border-b border-border pb-4" data-testid="review-item">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-forest">{r.name}</span>
                    <div className="flex">{[1,2,3,4,5].map((s) => <Star key={s} size={13} className={s <= r.rating ? "fill-gold text-gold" : "text-muted-foreground/30"} />)}</div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{r.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-sand rounded-2xl p-6">
          <h3 className="font-serif text-2xl text-forest mb-4">Write a Review</h3>
          <form onSubmit={submitReview} className="space-y-4">
            <div className="flex gap-1">
              {[1,2,3,4,5].map((s) => (
                <button type="button" key={s} onClick={() => setRating(s)} data-testid={`rating-star-${s}`}><Star size={24} className={s <= rating ? "fill-gold text-gold" : "text-muted-foreground/30"} /></button>
              ))}
            </div>
            <Input value={rName} onChange={(e) => setRName(e.target.value)} placeholder="Your name" data-testid="review-name" className="rounded-lg bg-white" />
            <textarea value={rComment} onChange={(e) => setRComment(e.target.value)} placeholder="Tell us what you think…" rows={4} data-testid="review-comment" className="w-full rounded-lg border border-border p-3 text-sm bg-white outline-none focus:border-gold" />
            <Button type="submit" data-testid="review-submit" className="bg-forest text-cream hover:bg-forest-light rounded-full px-8">Submit Review</Button>
          </form>
        </div>
      </div>
    </div>
  );
}
